import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, api } from '@/lib/api';
import { ensureNamespace } from '@/lib/i18n';

/**
 * The page a sign-in link opens.
 *
 * Opening it does nothing. Mail scanners — Outlook Safe Links and the rest —
 * fetch every URL in a message to check it, so a link that signed you in on
 * being loaded would be spent before the recipient had read the subject line.
 * Pressing Continue is the step that costs the token, and only a person can
 * press it.
 */
export const Route = createFileRoute('/login_/link')({
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search.token === 'string' ? { token: search.token } : {},
  beforeLoad: () => ensureNamespace('auth'),
  component: SignInLinkPage,
});

function SignInLinkPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(!token);

  const go = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const { destination } = await api<{ destination: string }>('/auth/email/consume', {
        method: 'POST',
        body: { token, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
      });
      // A full load, not a client navigation: the session cookie is new, and
      // every query cached before it belongs to nobody.
      window.location.assign(destination);
    } catch (err) {
      // Expired, spent and never-issued read the same here, as they do from the
      // API: which one it was is not the holder's business.
      if (err instanceof ApiRequestError) setFailed(true);
      else setFailed(true);
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-6 px-4">
      <h1 className="text-2xl font-semibold">{t('auth:link.title')}</h1>
      {failed ? (
        <>
          <p
            role="alert"
            className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {t('auth:link.invalid')}
          </p>
          <Link
            to="/login"
            className="flex min-h-12 items-center justify-center rounded border border-neutral-300 bg-white px-4 py-2 text-sm font-medium"
            onClick={() => navigate({ to: '/login' })}
          >
            {t('auth:link.backToLogin')}
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-neutral-600">{t('auth:link.body')}</p>
          <button
            type="button"
            disabled={busy}
            onClick={go}
            className="flex min-h-12 items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? t('common:status.loading') : t('auth:link.continue')}
          </button>
        </>
      )}
    </main>
  );
}
